/**
 * Layer-1 UTM forwarding (landing → app).
 *
 * Чистый helper — НЕ зависит от React-state хука useUtm. Источник UTM на момент
 * клика по любой CTA:
 *   1) текущие window.location.search параметры лендинга (utm_*, gclid, yclid, ref) —
 *      они есть ВСЕГДА при платном/реферальном заходе независимо от того, отработал ли
 *      useEffect хука useUtm → это закрывает тайм-рейс (CTA кликнута до завершения useEffect);
 *   2) если в URL нет ключа — fallback на чтение document.cookie (.transcripta.ru куки,
 *      выставленные useUtm при auto-detect / referral / повторном заходе).
 *
 * Дописывает только ОТСУТСТВУЮЩИЕ в baseUrl ключи — явные параметры baseUrl не перетираются.
 *
 * Инварианты utm-pipeline:
 *  - ровно 5 UTM-полей (utm_source/medium/campaign/term/content);
 *  - gclid/yclid — отдельные query-параметры, НЕ внутри utm_* (#2);
 *  - cookie чтение под root-domain куки, выставленные useUtm;
 *  - ym_uid берётся ТОЛЬКО из куки `_ym_uid` и валидируется как 17–19 цифр.
 */

const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"] as const;
// gclid/yclid — независимые identifier'ы (Google Ads / Yandex.Direct offline conversion),
// форвардятся отдельными query-параметрами, НЕ маппятся в utm_* (invariant #2).
const ID_KEYS = ["gclid", "yclid"] as const;
const REF_KEYS = ["ref"] as const;
// ClientID Метрики. Кука `_ym_uid` — first-party и живёт на домене лендинга,
// а приложение Audio2Text одно на все двенадцать доменов и лежит на
// audio-2-text.ru. Браузер чужую куку туда не отправляет, поэтому у зеркал
// ym_uid был пуст у 96–100% пользователей против 94% покрытия у Transcripta,
// где лендинг и приложение делят один домен. Провозим значение в ссылке.
const YM_KEYS = ["ym_uid"] as const;
const FORWARD_KEYS = [...UTM_KEYS, ...ID_KEYS, ...REF_KEYS, ...YM_KEYS] as const;

// Metrika ClientID — 17–19 цифр. Значение уезжает в users.ym_uid и оттуда в
// Measurement Protocol, поэтому мусор из URL пропускать нельзя.
const YM_UID_RE = /^\d{17,19}$/;

function readCookie(name: string): string | null {
  try {
    const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${name}=([^;]+)`));
    return match ? decodeURIComponent(match[1]) : null;
  } catch {
    return null;
  }
}

/**
 * Источник значения ключа: сначала URL лендинга, затем cookie.
 * Для `ref` cookie хранится под именем `referral_code`.
 */
function resolveValue(key: string, params: URLSearchParams): string | null {
  const fromUrl = params.get(key) || (key === "ref" ? params.get("referral") : null);
  if (key === "ym_uid") {
    // Только из куки: в URL лендинга ClientID не приходит, а если кто-то его
    // туда подставил — это не наш счётчик, и доверять ему нечего.
    const fromCookie = readCookie("_ym_uid");
    return fromCookie && YM_UID_RE.test(fromCookie) ? fromCookie : null;
  }
  if (fromUrl) return fromUrl;
  const cookieName = key === "ref" ? "referral_code" : key;
  return readCookie(cookieName);
}

/**
 * Дописывает UTM/gclid/yclid/ref/ym_uid к URL ссылки на app.
 * SSR-safe: вне браузера (нет window) возвращает baseUrl без изменений.
 */
export function appendUtmToUrl(baseUrl: string): string {
  if (typeof window === "undefined") return baseUrl;

  const params = new URLSearchParams(window.location.search);
  const collected: Array<[string, string]> = [];
  for (const key of FORWARD_KEYS) {
    const value = resolveValue(key, params);
    if (value) collected.push([key, value]);
  }
  if (collected.length === 0) return baseUrl;

  try {
    const url = new URL(baseUrl, window.location.origin);
    for (const [key, value] of collected) {
      if (!url.searchParams.has(key)) {
        url.searchParams.set(key, value);
      }
    }
    // Относительные пути остаются относительными.
    return baseUrl.startsWith("http") ? url.toString() : `${url.pathname}${url.search}${url.hash}`;
  } catch {
    const hashIndex = baseUrl.indexOf("#");
    const hash = hashIndex === -1 ? "" : baseUrl.slice(hashIndex);
    const withoutHash = hashIndex === -1 ? baseUrl : baseUrl.slice(0, hashIndex);
    const existing = withoutHash.includes("?") ? withoutHash.slice(withoutHash.indexOf("?") + 1) : "";
    const existingParams = new URLSearchParams(existing);
    const extra = collected
      .filter(([key]) => !existingParams.has(key))
      .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
      .join("&");
    if (!extra) return baseUrl;
    const sep = withoutHash.includes("?") ? "&" : "?";
    return `${withoutHash}${sep}${extra}${hash}`;
  }
}
